/**
 * Utility functions to compare current configs with published snapshots
 * to determine net changes (smart comparison)
 */

import { isUnconfiguredFieldRow } from '@/utils/fieldMappingCatalog'

/**
 * Compare current segments with published snapshot
 */
export function compareSegments(published: any[], current: any[]) {
  if (!published || published.length === 0) {
    // No published snapshot - all current segments are "changes"
    return {
      hasChanges: current && current.length > 0,
      added: current?.map((s: any) => ({ id: s.id, name: s.name })) || [],
      modified: [],
      removed: [],
      total: current?.length || 0,
    }
  }

  const added = current.filter((seg: any) => !published.find((pub: any) => pub.id === seg.id))

  const removed = published.filter((pub: any) => !current.find((seg: any) => seg.id === pub.id))

  const modified = current.filter((seg: any) => {
    const pub = published.find((p: any) => p.id === seg.id)
    return pub && JSON.stringify(pub) !== JSON.stringify(seg)
  })

  const hasChanges = added.length > 0 || removed.length > 0 || modified.length > 0

  return {
    hasChanges,
    added: added.map((s: any) => ({ id: s.id, name: s.name })),
    modified: modified.map((s: any) => ({ id: s.id, name: s.name })),
    removed: removed.map((s: any) => ({ id: s.id, name: s.name })),
    total: current.length,
  }
}

/**
 * Compare current field mappings with published snapshot
 * Published format (STRICT): { mapping_config: { all: { field_mapping: { mappings: [...] } } } }
 * Segments (all, new, used, cpo) are NESTED under mapping_config
 * Current format: { all: [{ name, values: { source, fallback } }] } (transformed UI format)
 */
/**
 * Normalize a source value to a string ID
 * Handles both string sources and object sources (for custom fields)
 */
function normalizeSource(source: any): string {
  if (!source) return ''
  if (typeof source === 'string') return source
  if (typeof source === 'object' && source.id) return source.id
  return String(source)
}

function getCurrentSources(field: any): string[] {
  return [
    normalizeSource(field.values?.source),
    ...(Array.isArray(field.values?.fallback)
      ? field.values.fallback.map((entry: any) => normalizeSource(entry?.source))
      : []),
  ].filter(Boolean)
}

/**
 * Normalize default_value to a comparable string.
 * - UI stores defaults as strings (often JSON-ish like "0", "[]", "\"text\"")
 * - Published config may store typed JSON (number/array/object/etc)
 */
function normalizeDefaultValue(v: any): string {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string') return v.trim()
  try {
    if (typeof v === 'object') return JSON.stringify(v)
    return String(v)
  } catch {
    return String(v)
  }
}

export function compareFieldMappings(
  published: any,
  current: any,
  activeSegment: string,
  opts?: { userConfigurableFields?: string[] },
) {
  // STRICT: Extract published mappings from mapping_config.{segment}.field_mapping.mappings
  // No fallback logic - if the path doesn't exist, treat as no published data
  const publishedMappings = published?.mapping_config?.[activeSegment]?.field_mapping?.mappings || []

  // Current is already in transformed format
  const currentFields = current?.[activeSegment] || []

  // The UI shows every editable field, including catalog fields the dealer has not
  // configured. Those empty rows (no source, no fallback, no default) carry no intent,
  // so ignore them unless they correspond to a published field (i.e. a real clear).
  const publishedFieldNames = new Set(publishedMappings.map((m: any) => m.field))
  const meaningfulCurrentFields = currentFields.filter(
    (f: any) => !isUnconfiguredFieldRow(f) || publishedFieldNames.has(f.name),
  )
  const allowRemovedSet =
    opts?.userConfigurableFields && Array.isArray(opts.userConfigurableFields)
      ? new Set(opts.userConfigurableFields.map(String))
      : null

  console.log('🔍 [compareFieldMappings] Debug:', {
    activeSegment,
    hasPublished: !!published,
    hasPublishedMappingConfig: !!published?.mapping_config,
    hasPublishedSegment: !!published?.mapping_config?.[activeSegment],
    hasFieldMapping: !!published?.mapping_config?.[activeSegment]?.field_mapping,
    publishedMappingsCount: publishedMappings.length,
    currentFieldsCount: currentFields.length,
    publishedMappingsSample: publishedMappings.slice(0, 2),
    currentFieldsSample: currentFields.slice(0, 2),
  })

  // STRICT: Check if published has the segment with proper structure under mapping_config
  if (
    !published?.mapping_config?.[activeSegment]?.field_mapping?.mappings ||
    publishedMappings.length === 0
  ) {
    console.log('⚠️ [compareFieldMappings] No published snapshot - treating all as changes')
    // No published snapshot - all configured modifications are "changes"
    const allFields = meaningfulCurrentFields.map((field: any) => ({
      field: field.name,
      sources: getCurrentSources(field),
      source: getCurrentSources(field)[0] || '',
      fallback: getCurrentSources(field)[1] || '',
      default: normalizeDefaultValue(field.policy?.default_value),
    }))
    return {
      hasChanges: allFields.length > 0,
      fieldsChanged: allFields,
      totalModified: allFields.length,
    }
  }

  const changedFields: any[] = []

  meaningfulCurrentFields.forEach((currentField: any) => {
    // Find matching field in published mappings (API format uses "field" property)
    const publishedMapping = publishedMappings.find((m: any) => m.field === currentField.name)

    if (!publishedMapping) {
      // Field was added
      changedFields.push({
        field: currentField.name,
        action: 'added',
        ...(getCurrentSources(currentField).length > 2
          ? { sources: getCurrentSources(currentField) }
          : {}),
        source: getCurrentSources(currentField)[0] || '',
        fallback: getCurrentSources(currentField)[1] || '',
      })
      return
    }

    const currentSources = getCurrentSources(currentField)
    const publishedSources = Array.isArray(publishedMapping.sources)
      ? publishedMapping.sources.map(normalizeSource).filter(Boolean)
      : []
    const currentSource = currentSources[0] || ''
    const publishedSource = publishedSources[0] || ''
    const currentFallback = currentSources[1] || ''
    const publishedFallback = publishedSources[1] || ''

    const sourceChanged = currentSource !== publishedSource
    const fallbackChanged = currentFallback !== publishedFallback
    const sourcesChanged = JSON.stringify(currentSources) !== JSON.stringify(publishedSources)
    const currentDefault = normalizeDefaultValue(currentField.policy?.default_value)
    const publishedDefault = normalizeDefaultValue(
      publishedMapping.default_value ?? publishedMapping.policy?.default_value,
    )
    const defaultChanged = currentDefault !== publishedDefault

    if (sourcesChanged || defaultChanged) {
      const change: any = { field: currentField.name }

      if (sourcesChanged && (publishedSources.length > 2 || currentSources.length > 2)) {
        change.sourcesChanged = { from: publishedSources, to: currentSources }
      }
      if (sourceChanged) {
        change.sourceChanged = { from: publishedSource, to: currentSource }
      }
      if (fallbackChanged) {
        change.fallbackChanged = { from: publishedFallback, to: currentFallback }
      }
      if (defaultChanged) {
        change.defaultChanged = { from: publishedDefault, to: currentDefault }
      }

      changedFields.push(change)
    }
  })

  // Check for removed fields
  publishedMappings.forEach((publishedMapping: any) => {
    const currentField = currentFields.find((f: any) => f.name === publishedMapping.field)
    if (!currentField) {
      // Always surface removal for custom fields (user-defined).
      const isCustom = !!publishedMapping?.custom
      // Option A: ignore removals for fields that are not user-configurable (computed/internal).
      const isUserConfigurable = allowRemovedSet ? allowRemovedSet.has(String(publishedMapping.field)) : true
      if (!isCustom && !isUserConfigurable) return

      changedFields.push({
        field: publishedMapping.field,
        action: 'removed',
      })
    }
  })

  return {
    hasChanges: changedFields.length > 0,
    fieldsChanged: changedFields,
    totalModified: changedFields.length,
  }
}

/**
 * Compare current publish rules with published snapshot
 */
export function compareListingRules(published: any, current: any) {
  if (!published || Object.keys(published).length === 0) {
    // No published snapshot - count all current rules
    const segments: any = {}
    let totalRules = 0

    for (const segmentId in current) {
      const rules = current[segmentId]?.inventory_membership_rules?.ruleset?.rules || []
      totalRules += rules.length
      if (rules.length > 0) {
        segments[segmentId] = {
          rulesCount: rules.length,
          rulesAdded: rules.length,
          rulesRemoved: 0,
          addedRules: rules.map((rule: any, idx: number) => formatRule(rule, idx)),
        }
      }
    }

    return {
      hasChanges: totalRules > 0,
      segments,
      totalRules,
      totalRulesAdded: totalRules,
      totalRulesRemoved: 0,
    }
  }

  const segments: any = {}
  let totalRules = 0
  let totalRulesAdded = 0
  let totalRulesRemoved = 0

  // Compare each segment
  for (const segmentId in current) {
    const currentSegment = current[segmentId]
    const publishedSegment = published[segmentId]

    const currentRules = currentSegment?.inventory_membership_rules?.ruleset?.rules || []
    const publishedRules = publishedSegment?.inventory_membership_rules?.ruleset?.rules || []

    totalRules += currentRules.length

    const addedRules = currentRules.filter(
      (rule: any) => !publishedRules.some((pub: any) => deepEqual(rule, pub)),
    )

    const removedRules = publishedRules.filter(
      (pub: any) => !currentRules.some((rule: any) => deepEqual(rule, pub)),
    )

    const modifiedRules: any[] = []
    // Only detect "modified" when there are no adds/removes to avoid false positives from reordering
    if (addedRules.length === 0 && removedRules.length === 0) {
      currentRules.forEach((rule: any, idx: number) => {
        const publishedRule = publishedRules[idx]
        if (publishedRule && !deepEqual(rule, publishedRule)) {
          modifiedRules.push({
            index: idx,
            before: formatRule(publishedRule, idx),
            after: formatRule(rule, idx),
          })
        }
      })
    }

    totalRulesAdded += addedRules.length
    totalRulesRemoved += removedRules.length

    if (addedRules.length > 0 || removedRules.length > 0 || modifiedRules.length > 0) {
      segments[segmentId] = {
        rulesCount: currentRules.length,
        rulesAdded: addedRules.length,
        rulesRemoved: removedRules.length,
      }

      if (addedRules.length > 0) {
        const formatted = addedRules.map((rule: any) =>
          formatRule(rule, currentRules.indexOf(rule)),
        )
        // Dedupe by field|operator|value to avoid duplicates (e.g., group vs leaf)
        const seen = new Set<string>()
        const unique = formatted.filter((r: any) => {
          const sig = `${r.field}|${r.operator}|${String(r.value)}`
          if (seen.has(sig)) return false
          seen.add(sig)
          return true
        })
        segments[segmentId].addedRules = unique
        segments[segmentId].rulesAdded = unique.length
      }

      if (removedRules.length > 0) {
        segments[segmentId].removedRules = removedRules.map((rule: any, idx: number) =>
          formatRule(rule, idx),
        )
      }

      if (modifiedRules.length > 0) {
        segments[segmentId].modifiedRules = modifiedRules
      }
    }
  }

  // Check for removed segments
  for (const segmentId in published) {
    if (!current[segmentId]) {
      const publishedRules = published[segmentId]?.inventory_membership_rules?.ruleset?.rules || []
      totalRulesRemoved += publishedRules.length
      segments[segmentId] = {
        rulesCount: 0,
        rulesAdded: 0,
        rulesRemoved: publishedRules.length,
        removedRules: publishedRules.map((rule: any, idx: number) => formatRule(rule, idx)),
        segmentRemoved: true,
      }
    }
  }

  // Recalculate totals after dedupe to ensure accurate counts
  let dedupedAddedTotal = 0
  let removedTotal = 0
  for (const key in segments) {
    const seg = segments[key]
    dedupedAddedTotal += Array.isArray(seg.addedRules) ? seg.addedRules.length : seg.rulesAdded || 0
    removedTotal += Array.isArray(seg.removedRules)
      ? seg.removedRules.length
      : seg.rulesRemoved || 0
  }
  const hasChanges = Object.keys(segments).length > 0

  return {
    hasChanges,
    segments,
    totalRules,
    totalRulesAdded: dedupedAddedTotal,
    totalRulesRemoved: removedTotal,
  }
}

/**
 * Format a rule for comparison output
 */
function formatRule(rule: any, index: number) {
  // Prefer leaf rule details when a single-rule group is provided
  let field = rule.field
  let operator = rule.operator
  let value = rule.value
  if ((!field || !operator) && Array.isArray(rule.rules) && rule.rules.length > 0) {
    const first = rule.rules[0]
    field = first.field || field
    operator = first.operator || operator || rule.logic
    value = first.value !== undefined ? first.value : value
  }
  return {
    index,
    field,
    operator: operator || rule.logic,
    value,
    action: rule.action,
    reason: rule.reason || '',
  }
}

/**
 * Deep equality check for objects
 */
function deepEqual(obj1: any, obj2: any): boolean {
  return JSON.stringify(normalizeForListingRuleCompare(obj1)) === JSON.stringify(normalizeForListingRuleCompare(obj2))
}

/**
 * Normalize listing-rule nodes so comparisons are stable across:
 * - object key ordering differences (common after save/load)
 * - metadata fields that don't impact rule semantics
 * - id fields (for patch-based revert; should not affect semantic equality)
 *
 * This is intentionally scoped to publish rules comparisons only.
 */
function normalizeForListingRuleCompare(node: any): any {
  if (node == null) return node
  if (Array.isArray(node)) return node.map(normalizeForListingRuleCompare)
  if (typeof node !== 'object') return node

  // Leaf rule (strip id for comparison)
  if (node.type === 'rule') {
    return {
      type: 'rule',
      field: node.field,
      operator: node.operator,
      value: normalizeForListingRuleCompare(node.value),
      action: node.action,
      reason: node.reason ?? '',
    }
  }

  // Group rule (strip id for comparison)
  if (node.type === 'group') {
    return {
      type: 'group',
      logic: node.logic,
      action: node.action,
      reason: node.reason ?? '',
      rules: Array.isArray(node.rules) ? node.rules.map(normalizeForListingRuleCompare) : [],
    }
  }

  // Fallback: stable key order for any unknown objects (including value objects)
  const out: any = {}
  for (const k of Object.keys(node).sort()) {
    if (k === 'id') continue // skip id for semantic comparison
    out[k] = normalizeForListingRuleCompare(node[k])
  }
  return out
}
